import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import {
  Button,
  Card,
  Field,
  FormAction,
  FormGrid,
  MIN_PASSWORD,
  friendlyAuthError,
} from '@crave/ui';
import {
  onSession,
  sendReauthCode,
  signOutEverywhere,
  supabase,
  updatePassword,
} from '../lib/auth.ts';
import { STAFF_ME_KEY } from '../lib/staff.tsx';
import { Notice, Section } from '../components/ui.tsx';

/** Code and password fields: two columns side by side on desktop, one on a phone. */
const CODE_COL = 220;
const CODE_MAX = 480;
/** A code grid ends its card: drop the 16px padding of the grid's last row. */
const CODE_GRID = { maxWidth: CODE_MAX, marginBottom: -16 };

/**
 * Two-factor authentication with an authenticator app (TOTP). On the production API, payment and
 * email settings, Crave Credit rules and unit-economics numbers require a session that completed a
 * second factor.
 */
export function Security() {
  const qc = useQueryClient();
  const [factors, setFactors] = useState<
    { id: string; friendly_name: string | undefined; status: string }[]
  >([]);
  const [aal, setAal] = useState<string | null>(null);
  const [enroll, setEnroll] = useState<{ id: string; qr: string; secret: string } | null>(null);
  const [code, setCode] = useState('');
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);

  const load = async () => {
    if (!supabase) return;
    const { data } = await supabase.auth.mfa.listFactors();
    setFactors(
      (data?.totp ?? []).map((f) => ({
        id: f.id,
        friendly_name: f.friendly_name,
        status: f.status,
      })),
    );
    const level = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
    setAal(level.data?.currentLevel ?? null);
  };
  useEffect(() => {
    void load();
  }, []);

  /** The shell banner and page permissions read the session level from /staff/me. */
  const refreshStaff = () => qc.invalidateQueries({ queryKey: [STAFF_ME_KEY] });

  async function start() {
    if (!supabase) return;
    setMsg(null);
    const { data, error } = await supabase.auth.mfa.enroll({
      factorType: 'totp',
      friendlyName: 'Authenticator app',
    });
    if (error || !data) {
      setMsg({ kind: 'error', text: error?.message ?? 'Could not start enrollment.' });
      return;
    }
    setEnroll({ id: data.id, qr: data.totp.qr_code, secret: data.totp.secret });
  }
  async function verify() {
    if (!supabase || !enroll) return;
    setMsg(null);
    const { error } = await supabase.auth.mfa.challengeAndVerify({
      factorId: enroll.id,
      code: code.trim(),
    });
    if (error) {
      setMsg({ kind: 'error', text: error.message });
      return;
    }
    setEnroll(null);
    setCode('');
    setMsg({ kind: 'ok', text: 'Two-factor authentication is on for this account.' });
    await load();
    await refreshStaff();
  }
  async function stepUp(factorId: string) {
    if (!supabase) return;
    setMsg(null);
    const { error } = await supabase.auth.mfa.challengeAndVerify({ factorId, code: code.trim() });
    if (error) {
      setMsg({ kind: 'error', text: error.message });
      return;
    }
    setCode('');
    setMsg({ kind: 'ok', text: 'Verified for this session.' });
    await load();
    await refreshStaff();
  }
  async function remove(id: string) {
    if (!supabase) return;
    if (aal !== 'aal2') {
      setMsg({
        kind: 'error',
        text: 'Enter a code below to verify this session, then remove the authenticator.',
      });
      return;
    }
    if (!confirm('Remove this authenticator?')) return;
    setMsg(null);
    const { error } = await supabase.auth.mfa.unenroll({ factorId: id });
    if (error) {
      setMsg({ kind: 'error', text: `Could not remove the authenticator: ${error.message}` });
      return;
    }
    setMsg({ kind: 'ok', text: 'Authenticator removed.' });
    await load();
    await refreshStaff();
  }

  const verified = factors.filter((f) => f.status === 'verified');
  return (
    <Section title="Security">
      {msg && <Notice kind={msg.kind}>{msg.text}</Notice>}
      <Card>
        <strong>Two-factor authentication</strong>
        <p style={{ margin: '4px 0 12px', fontSize: 14, color: 'var(--ink-soft)' }}>
          Required on the live system for payment and email settings, Crave Credit rules and
          unit-economics numbers. Current session level: <code>{aal ?? '…'}</code>.
        </p>
        {verified.length === 0 && !enroll && (
          <Button onClick={start}>Set up an authenticator app</Button>
        )}
        {enroll && (
          <div style={{ display: 'grid', gap: 12 }}>
            <p style={{ margin: 0 }}>
              Scan this with 1Password, Google Authenticator, or Authy, then enter the 6-digit code.
            </p>
            <img src={enroll.qr} alt="Authenticator QR code" width={200} height={200} />
            <details>
              <summary style={{ cursor: 'pointer' }}>Can't scan? Enter the key manually</summary>
              <code>{enroll.secret}</code>
            </details>
            <FormGrid min={CODE_COL} style={CODE_GRID}>
              <Field
                label="6-digit code"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
              <FormAction>
                <Button onClick={verify} disabled={code.trim().length < 6}>
                  Turn on
                </Button>
              </FormAction>
            </FormGrid>
          </div>
        )}
        {verified.length > 0 && (
          <div style={{ display: 'grid', gap: 12 }}>
            {verified.map((f) => (
              <div
                key={f.id}
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              >
                <span>✓ {f.friendly_name ?? 'Authenticator'} enrolled</span>
                <Button
                  variant="danger"
                  onClick={() => void remove(f.id)}
                  title={aal !== 'aal2' ? 'Verify this session first' : undefined}
                >
                  Remove
                </Button>
              </div>
            ))}
            {aal !== 'aal2' && (
              <div style={{ display: 'grid', gap: 8 }}>
                <p style={{ margin: 0 }}>
                  Enter a code to verify this session. You need this before changing protected
                  settings or removing the authenticator.
                </p>
                <FormGrid min={CODE_COL} style={CODE_GRID}>
                  <Field
                    label="6-digit code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                  />
                  <FormAction>
                    <Button
                      onClick={() => stepUp(verified[0]!.id)}
                      disabled={code.trim().length < 6}
                    >
                      Verify
                    </Button>
                  </FormAction>
                </FormGrid>
              </div>
            )}
          </div>
        )}
      </Card>
      <PasswordCard />
    </Section>
  );
}

/** Change your own password. Supabase may ask for an emailed code first (secure password change). */
function PasswordCard() {
  const [email, setEmail] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [show, setShow] = useState(false);
  const [nonce, setNonce] = useState('');
  const [needsCode, setNeedsCode] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  useEffect(() => onSession((s) => setEmail(s?.user.email ?? null)), []);

  async function save() {
    if (password.length < MIN_PASSWORD) {
      setMsg({ kind: 'error', text: `Use at least ${MIN_PASSWORD} characters.` });
      return;
    }
    if (password !== confirmPassword) {
      setMsg({ kind: 'error', text: "The two passwords don't match." });
      return;
    }
    setBusy(true);
    setMsg(null);
    const r = await updatePassword(password, needsCode ? nonce.trim() : undefined);
    if (r.needsCode && !needsCode) {
      const err = await sendReauthCode();
      setNeedsCode(true);
      setMsg(
        err
          ? { kind: 'error', text: friendlyAuthError(err) }
          : {
              kind: 'ok',
              text: `For security we emailed a code to ${email ?? 'your email'}. Enter it below.`,
            },
      );
    } else if (r.error) setMsg({ kind: 'error', text: friendlyAuthError(r.error) });
    else if (r.needsCode)
      setMsg({ kind: 'error', text: 'That code did not work. Check the newest email.' });
    else {
      setPassword('');
      setConfirmPassword('');
      setNonce('');
      setNeedsCode(false);
      setMsg({
        kind: 'ok',
        text: 'Password changed. Use it next time you sign in, here, in the kitchen, or when ordering.',
      });
    }
    setBusy(false);
  }

  return (
    <Card>
      <div style={{ display: 'grid', gap: 12, maxWidth: CODE_MAX }}>
        <strong>Password</strong>
        <p style={{ margin: 0, fontSize: 14, color: 'var(--ink-soft)' }}>
          Signed in as {email ?? '…'}. The same login works in the admin, the kitchen and on the
          ordering site.
        </p>
        {msg && <Notice kind={msg.kind}>{msg.text}</Notice>}
        <div style={{ display: 'grid', gap: 0 }}>
          <FormGrid min={CODE_COL}>
            <Field
              label="New password"
              type={show ? 'text' : 'password'}
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              minLength={MIN_PASSWORD}
              hint={`At least ${MIN_PASSWORD} characters.`}
            />
            <Field
              label="Type it again"
              type={show ? 'text' : 'password'}
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
            {needsCode && (
              <Field
                label="Code from the email"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={nonce}
                onChange={(e) => setNonce(e.target.value.replace(/\s/g, ''))}
              />
            )}
          </FormGrid>
          <label
            style={{
              display: 'flex',
              gap: 10,
              alignItems: 'center',
              minHeight: 44,
              fontSize: 14,
              cursor: 'pointer',
              justifySelf: 'start',
            }}
          >
            <input
              type="checkbox"
              checked={show}
              onChange={(e) => setShow(e.target.checked)}
              style={{ width: 18, height: 18, margin: 0, flex: 'none' }}
            />
            Show password
          </label>
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <Button onClick={() => void save()} disabled={busy || password.length === 0}>
            {busy ? 'Saving…' : 'Change password'}
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              if (confirm('Sign out on every device, including this one?'))
                void signOutEverywhere();
            }}
          >
            Sign out of all devices
          </Button>
        </div>
      </div>
    </Card>
  );
}
